import React from "react";

export type PaginationBarProps = {
  from: number;
  to: number;
  total: number;
  currentPage?: number;
  /** If omitted: 1 when total &gt; 0, otherwise 0. */
  totalPages?: number;
  onPrev?: () => void;
  onNext?: () => void;
  onPageSelect?: (page: number) => void;
  className?: string;
};

function pageWindow(current: number, totalPages: number): number[] {
  if (totalPages <= 0) return [];
  const windowSize = 5;
  let start = Math.max(1, current - Math.floor(windowSize / 2));
  const end = Math.min(totalPages, start + windowSize - 1);
  start = Math.max(1, end - windowSize + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function PaginationBar({
  from,
  to,
  total,
  currentPage = 1,
  totalPages: totalPagesProp,
  onPrev,
  onNext,
  onPageSelect,
  className = "",
}: PaginationBarProps) {
  const totalPages =
    totalPagesProp !== undefined
      ? totalPagesProp
      : total > 0
        ? 1
        : 0;

  const canPrev = totalPages > 0 && currentPage > 1;
  const canNext = totalPages > 0 && currentPage < totalPages;
  const visiblePages = pageWindow(currentPage, totalPages);

  return (
    <div
      className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between m-4 text-sm ${className}`}
    >
      <p className="text-gray-500">
        Showing {total === 0 ? 0 : from} to {to} of {total} entries
      </p>

      {totalPages > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={!canPrev || !onPrev}
            onClick={() => canPrev && onPrev?.()}
            className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>

          <div className="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50/80 p-0.5 shadow-sm">
            {visiblePages.map((page) => {
              const active = page === currentPage;
              if (active) {
                return (
                  <span
                    key={page}
                    aria-current="page"
                    className="inline-flex min-h-[2.25rem] min-w-[2.25rem] items-center justify-center rounded-md bg-vg-blue text-sm font-medium text-white shadow-sm"
                  >
                    {page}
                  </span>
                );
              }
              return (
                <button
                  key={page}
                  type="button"
                  disabled={!onPageSelect}
                  onClick={() => onPageSelect?.(page)}
                  className={`inline-flex min-h-[2.25rem] min-w-[2.25rem] items-center justify-center rounded-md text-sm font-medium transition-colors ${
                    onPageSelect
                      ? "text-gray-700 hover:bg-white"
                      : "cursor-default text-gray-400"
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            disabled={!canNext || !onNext}
            onClick={() => canNext && onNext?.()}
            className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
